import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  AbstractFieldInputTypeHandlerComponent, FormValueReadableValue,
} from "../../../../../../components/common/field/field-input/type-handler/abstract-field-input-type-handler.component";
import {FormBuilder, FormGroup} from "@angular/forms";
import {FormDefinitionFieldModel} from "../../../../../form/definition/field/api/entity/formDefinitionField.model";
import {FormValueModel} from "../../../../../form/form/value/api/entity/formValue.model";
import {
  MaterializePlateExamDynamicComponent,
  MaterializePlateExamDynamicComponentData, MaterializePlateExamDynamicComponentDataFoot,
} from "../../../api/component/materialize-plate-exam-dynamic/materialize-plate-exam-dynamic.component";
import {ButtonModule} from "../../../../../core/component/button/button.module";
import {
  MaterializePlateExamDynamicFootComparisonDialogComponent,
  MaterializePlateExamDynamicFootComparisonDialogInput,
} from "../materialize-plate-exam-dynamic-foot-comparison-dialog/materialize-plate-exam-dynamic-foot-comparison-dialog.component";
import {DialogController} from "../../../../../common/dialog/dialogController";
import {
  AbstractDrawableImageV2InputTypeHandler, DrawablePayload
} from "../../../../../../components/common/field/field-input/type-handlers/drawable-image/v2/AbstractDrawableImageV2InputTypeHandler";
import {ImageHelper} from "../../../../../common/image/imageHelper";
import {Base64Utils} from "../../../../../core/utils/base64.utils";
import {MaterializePlateExamControl} from "../../control/materializePlateExam.control";

@Component({
  selector: 'app-materialize-plate-exam-dynamic-field-input-handler',
  standalone: true,
  imports: [CommonModule, MaterializePlateExamDynamicComponent, ButtonModule],
  templateUrl: './materialize-plate-exam-dynamic-field-input-handler.component.html',
  styles: [
  ]
})
export class MaterializePlateExamDynamicFieldInputHandlerComponent extends AbstractFieldInputTypeHandlerComponent<FormGroup> {
  data: MaterializePlateExamDynamicComponentData;
  initialData: JsonPressureMeasurement;
  public static resolveReadableValues(value: any): Promise<FormValueReadableValue[]> {
    if (!value) {
      return Promise.resolve([]);
    }
    console.log(value);
    const parsed = JSON.parse(value as string);
    return new Promise<FormValueReadableValue[]>((resolve, reject) => {
      Promise.all([
        MaterializePlateExamDynamicFieldInputHandlerComponent.buildImageReadableValue(parsed.left),
        MaterializePlateExamDynamicFieldInputHandlerComponent.buildImageReadableValue(parsed.right)]
      ).then(values => {
        const readableValues: FormValueReadableValue[] = [];
        if (values[0]) {
          readableValues.push(FormValueReadableValue.ofImage(values[0]));
        }
        if (values[1]) {
          readableValues.push(FormValueReadableValue.ofImage(values[1]));
          resolve(readableValues);
        }
      }).catch(e => reject(e));
    })
  }

  static buildImageReadableValue(side: any): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (! side.image) {
        resolve(undefined);
      }
      const srcImage = side.image;
      const drawableData = side.drawableData;
      const gravityCenters: {x: number, y: number}[] = side.gravityCenters;
      const frames: any[] = side.frames;

      const imageElement = new Image();
      imageElement.src = srcImage;
      imageElement.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = imageElement.width;
        canvas.height = imageElement.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(imageElement, 0, 0);
        new MaterializePlateExamControl().drawCenterOfGravities(canvas, gravityCenters, frames[0].data, side.cellWidth, side.cellHeight, 2, 2);


        if (!drawableData?.image) {
          resolve(canvas.toDataURL());
        } else {
          ImageHelper.combineImagesFromBase64(canvas.toDataURL(), drawableData.image)
              .then((value) => {
                Base64Utils.blobToBase64(value)
                    .then(base64 => {
                      resolve(base64);
                    })
                    .catch(e => reject(e))
              })
              .catch((reason) => reject(reason));
        }
      }
    });
  }

  constructor(private formBuilder: FormBuilder,
              private dialogController: DialogController) {
    super();
  }

  buildJsonControl(formDefinitionField: FormDefinitionFieldModel): FormGroup {
    return this.formBuilder.group({
      value: this.formBuilder.control(undefined),
    });
  }

  initiateForm(formValue: FormValueModel): void {
    this.formValue = undefined;
    if (formValue.value) {
      this.jsonControl.get('value').setValue(formValue.value);
      this.initialData = JSON.parse(formValue.value);
    }
  }

  synchronizeValue(): string {
    return this.jsonControl.get('value').value;
  }

  changed(value: MaterializePlateExamDynamicComponentData){
    this.data = value;
    if (this.data) {
      const json = new JsonPressureMeasurement(value);
      this.jsonControl.get('value').setValue(JSON.stringify(json));
    }
  }

  openComparison() {
    if (this.initialData && this.initialData.left?.frames?.length > 0 && this.initialData.right?.frames?.length > 0) {
      const input: MaterializePlateExamDynamicFootComparisonDialogInput = {
        sensorHeight: this.initialData.plate.sensorHeight,
        sensorWidth: this.initialData.plate.sensorWidth,
        frameRate: this.initialData.plate.frameRate,
        feet: [{
          type: 'left',
          picture: this.initialData.left?.image,
          durationInMillis: +this.initialData.left?.durationInMillis,
          data: {
            frames: this.initialData.left.frames,
            cellHeight: this.initialData.left.cellHeight,
            cellWidth: this.initialData.left.cellWidth
          },
          drawableData: !this.initialData.left.drawableData ? undefined : {
            items: this.initialData.left.drawableData.data.items.map(item => {
              return {
                typeValue: item.typeValue,
                subTypeValue: item.subTypeValue,
                common: item.common,
                properties: item.properties
              };
            }),
          }
        }, {
          type: 'right',
          picture: this.initialData.right?.image,
          durationInMillis: +this.initialData.right?.durationInMillis,
          data: {
            frames: this.initialData.right.frames,
            cellHeight: this.initialData.right.cellHeight,
            cellWidth: this.initialData.right.cellWidth
            },
          drawableData: !this.initialData.right.drawableData ? undefined : {
            items: this.initialData.right.drawableData.data.items.map(item => {
              return {
                typeValue: item.typeValue,
                subTypeValue: item.subTypeValue,
                common: item.common,
                properties: item.properties
              };
            }),
          }
          }
        ]
      }
      this.dialogController.open(MaterializePlateExamDynamicFootComparisonDialogComponent, input, {
        title: 'Comparaison',
        width: '95%'
      })
    }
  }
}

class JsonPressureData {
  data: number[][];
}

class JsonGravityCenter {
  x: number;
  y: number;
}

class JsonSideData {
  cellWidth: number;
  cellHeight: number;
  image: string;
  frames: JsonPressureData[];
  gravityCenters: JsonGravityCenter[];
  durationInMillis: number;
  drawableData: DrawablePayload;
}

class JsonPlate {
  id: number;
  brand: string;
  firmware: string;
  licensed: string;
  model: string;
  serial: string;
  width: number;
  height: number;
  sensorWidth: number;
  sensorHeight: number;
  frameRate: number;
}

class JsonPressureMeasurement {
  left: JsonSideData;
  right: JsonSideData;
  plate: JsonPlate;

  constructor(value: MaterializePlateExamDynamicComponentData) {
    this.plate = {
      id: value.plate.id,
      brand: value.plate.brand,
      firmware: value.plate.firmware,
      licensed: value.plate.licensed,
      model: value.plate.model,
      serial: value.plate.serial,
      width: value.plate.width,
      height: value.plate.height,
      sensorWidth: value.plate.sensorWidth,
      sensorHeight: value.plate.sensorHeight,
      frameRate: value.plate.frameRate,
    };

    const createSideData = (side: MaterializePlateExamDynamicComponentDataFoot): JsonSideData => ({
      cellWidth: value.plate.sensorWidth,
      cellHeight: value.plate.sensorHeight,
      image: side.image,
      frames: side.pressureData.frames.map((frame: any) => ({ data: frame.data })),
      gravityCenters: side.gravityCenters.map((gc: any) => ({ x: gc.x, y: gc.y })),
      durationInMillis: side.durationInMillis,
      drawableData: AbstractDrawableImageV2InputTypeHandler.buildPayload(side.drawableData)
    });

    this.left = createSideData(value.left);
    this.right = createSideData(value.right);
  }
}

